/**
 * MidiPicker — topbar device selector + status indicator.
 *
 * Reflects engine state in one widget:
 *   - "MIDI not supported" — browser doesn't have Web MIDI.
 *   - "MIDI: denied" — user refused permission.
 *   - "MIDI" + dropdown of devices — engine ready; pick which input(s) drive
 *     the synth. Options: None, All, and each connected device.
 *
 * Persists the user's last selection in localStorage so re-opens reconnect
 * automatically (subject to Web MIDI permission still being granted).
 */

import type { ActiveInput, MidiEngine, MidiInputInfo } from '@ac-slayer/synth-midi';

const STORAGE_KEY = 'evil-synth:midi-input';

export interface MidiPickerOptions {
  readonly engine: MidiEngine;
  /** Called on explicit user request — permission prompts require a gesture. */
  readonly onRequestPermission: () => Promise<void>;
}

export class MidiPicker {
  readonly element: HTMLElement;
  private readonly engine: MidiEngine;
  private readonly onRequestPermission: () => Promise<void>;

  private readonly select: HTMLSelectElement;
  private readonly statusEl: HTMLSpanElement;
  private readonly enableBtn: HTMLButtonElement;

  private readonly unsubscribeState: () => void;
  private readonly unsubscribeInputs: () => void;

  constructor(opts: MidiPickerOptions) {
    this.engine = opts.engine;
    this.onRequestPermission = opts.onRequestPermission;

    this.element = document.createElement('label');
    this.element.className = 'midi-picker';

    const label = document.createElement('span');
    label.className = 'label';
    label.textContent = 'MIDI';
    this.element.appendChild(label);

    this.select = document.createElement('select');
    this.select.className = 'midi-picker-select mono';
    this.select.setAttribute('aria-label', 'MIDI input device');
    this.select.addEventListener('change', () => {
      const value = this.select.value as ActiveInput;
      this.engine.setActiveInput(value);
      persistSelection(value);
    });
    this.element.appendChild(this.select);

    this.statusEl = document.createElement('span');
    this.statusEl.className = 'midi-picker-status label';
    this.element.appendChild(this.statusEl);

    this.enableBtn = document.createElement('button');
    this.enableBtn.type = 'button';
    this.enableBtn.className = 'btn btn-sm';
    this.enableBtn.textContent = 'Enable';
    this.enableBtn.addEventListener('click', () => void this.onRequestPermission());
    this.element.appendChild(this.enableBtn);

    this.unsubscribeState = this.engine.onStateChange((state) =>
      this.renderState(state.kind),
    );
    this.unsubscribeInputs = this.engine.onInputsChange((inputs) =>
      this.renderInputs(inputs),
    );
  }

  dispose(): void {
    this.unsubscribeState();
    this.unsubscribeInputs();
  }

  private renderState(kind: 'unsupported' | 'pending' | 'denied' | 'ready'): void {
    switch (kind) {
      case 'unsupported':
        this.select.hidden = true;
        this.enableBtn.hidden = true;
        this.statusEl.hidden = false;
        this.statusEl.textContent = 'not supported';
        break;
      case 'pending':
        this.select.hidden = true;
        this.enableBtn.hidden = false;
        this.enableBtn.disabled = false;
        this.statusEl.hidden = true;
        break;
      case 'denied':
        this.select.hidden = true;
        this.enableBtn.hidden = false;
        this.enableBtn.disabled = false;
        this.statusEl.hidden = false;
        this.statusEl.textContent = 'denied';
        break;
      case 'ready':
        this.select.hidden = false;
        this.enableBtn.hidden = true;
        this.statusEl.hidden = true;
        break;
    }
  }

  private renderInputs(inputs: readonly MidiInputInfo[]): void {
    const previous = this.select.value || loadSelection();
    this.select.innerHTML = '';

    const addOption = (value: string, text: string): void => {
      const o = document.createElement('option');
      o.value = value;
      o.textContent = text;
      this.select.appendChild(o);
    };

    addOption('none', 'None');
    addOption('all', `All (${inputs.length})`);
    for (const input of inputs) addOption(input.id, input.name);

    // Prefer the previously selected value if it still exists, else fall back
    // to 'all' when there's at least one device, else 'none'.
    const candidates = [previous, 'all', 'none'];
    for (const c of candidates) {
      if (c && Array.from(this.select.options).some((o) => o.value === c)) {
        this.select.value = c;
        break;
      }
    }
    this.engine.setActiveInput(this.select.value as ActiveInput);
  }
}

function persistSelection(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* quota / privacy mode — ignore */
  }
}

function loadSelection(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
